// To replace original ApplicationsTable.tsx

import { ComponentHeader } from '@/catalog-apps/components/component-library/shared/ComponentHeader'
import { SearchInput } from '@/catalog-apps/components/component-library/shared/SearchInput'
import { Button } from '@/components/shadcn/button'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { ToggleGroup, ToggleGroupItem } from '@/components/shadcn/toggle-group'
import { useRestClient } from '@/contexts/QueryProvider'
import { ErrorComponent } from '@/ui-legacy/ErrorComponent'
import { resolveAppLaunchKind } from '@/util/appTypes'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { AnimatePresence } from 'motion/react'
import { useMemo, useState } from 'react'
import { ApplicationsGridItem } from './ApllicationsGridItem'
import { ApplicationForm } from '../Forms/ApplicationForm'
import { NoSearchResultsDisplay } from '@/components/ui/NoSeachResultsDisplay'
import { Skeleton } from '@/components/shadcn/skeleton'
import { ComponentTitle } from '@/catalog-apps/components/component-library/shared/ComponentTitle'
import { useOutletContext } from 'react-router'
import { ApplicationsOutletContext } from '@/layouts/ApplicationsRootLayout'
import { GridTableToggle } from './GridTableToggle'

export const presenceAnimation = {
  initial: { opacity: 0, scale: 0.8, y: 20 },
  animate: { opacity: 1, scale: 1, y: 0 },
  // exit: { opacity: 0, scale: 0.8, y: -20 },
  transition: { duration: 0.15, ease: 'easeOut' as const },
}

export function ApplicationsGrid() {
  const { getApplicationsApiGetApplications } = useRestClient()
  const { data: applications } = getApplicationsApiGetApplications.useSuspenseQuery(undefined)
  const [searchString, setSearchString] = useState<string>('')
  const [appType, setAppType] = useState<AppType>('')
  const { view, setView } = useOutletContext<ApplicationsOutletContext>()
  const filteredApplicationsIds = useMemo(
    () =>
      applications
        .filter((application) => {
          if (!searchString && !appType) return true
          const nameMatch = application.appName.toLowerCase().includes(searchString.toLowerCase())
          const descriptionMatch = application.description.toLowerCase().includes(searchString.toLowerCase())
          const keyMatch = application.appKey.toLowerCase().includes(searchString.toLowerCase())
          const appTypeMatch =
            appType === '' ||
            (appType === 'embedded' && resolveAppLaunchKind(application.appTypes) === 'embedded') ||
            (appType === 'catalog' && resolveAppLaunchKind(application.appTypes) === 'catalog') ||
            (appType === 'unity' && resolveAppLaunchKind(application.appTypes) === 'unity')
          return (nameMatch || descriptionMatch || keyMatch) && appTypeMatch
        })
        .map((application) => application.applicationId),
    [applications, searchString, appType]
  )
  return (
    <ScrollArea className="h-full">
      <div className="sticky top-0 z-10 flex w-full justify-between border-b border-offwhite bg-offwhite/80 backdrop-blur-sm">
        <ComponentHeader className="border-b-0 pt-5">
          <ComponentTitle className="pl-5" title="Applications" />
          <div className="ml-auto mr-0 flex items-center gap-4">
            <div className="flex items-center gap-x-2">
              <ToggleGroup
                // variant="outline"
                className="h-[35px] rounded-md border bg-background p-0.5"
                type="single"
                value={appType}
                onValueChange={(value) => setAppType(value as AppType)}
              >
                <ToggleGroupItem className="h-[28px]" value="embedded">
                  Embedded
                </ToggleGroupItem>
                <ToggleGroupItem className="h-[28px]" value="catalog">
                  Catalog
                </ToggleGroupItem>
                <ToggleGroupItem className="h-[28px]" value="unity">
                  Unity
                </ToggleGroupItem>
              </ToggleGroup>
              <SearchInput value={searchString} onChange={setSearchString} />
              <ApplicationForm
                sheetTitle="Add Application"
                sheetTrigger={
                  <Button size="lg" variant="outline">
                    <IconPlusOutline className="mr-2" />
                    Add Application
                  </Button>
                }
              />
              <GridTableToggle value={view} onChange={setView} />
            </div>
          </div>
        </ComponentHeader>
      </div>
      <AnimatePresence>
        {filteredApplicationsIds.length > 0 ? (
          <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-8 pb-5">
            {applications.map((application) => (
              <ApplicationsGridItem
                key={application.applicationId}
                application={application}
                className={filteredApplicationsIds.includes(application.applicationId) ? '' : 'hidden'}
              />
            ))}
          </div>
        ) : (
          <NoSearchResultsDisplay />
        )}
      </AnimatePresence>
    </ScrollArea>
  )
}

type AppType = 'embedded' | 'catalog' | 'unity' | ''

export function ApplicationsGridError() {
  return <ErrorComponent />
}

export function ApplicationsGridLoading() {
  return (
    <>
      <ComponentHeader className="border-b-0 pt-5">
        <div className="ml-auto mr-0 flex items-center gap-4">
          <div className="flex items-center gap-x-2">
            <Skeleton className="h-[35px] w-[150px]" />
          </div>
        </div>
      </ComponentHeader>
      <div className="grid w-full grid-cols-[repeat(auto-fill,minmax(350px,1fr))] gap-8 pb-5">
        {Array.from({ length: 10 }).map((_, index) => (
          <Skeleton key={index} className="h-[192px] w-full" />
        ))}
      </div>
    </>
  )
}
